import React from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";

interface RadarDataPoint {
  subject: string;
  value: number;
}

// 2. 定义组件 Props 的接口
interface ScoreRadarChartProps {
  scores: RadarDataPoint[];
  type: "market" | "risk" | "whale";
  baselineScore?: number;
}

// 3. 定义 Recharts 内部需要的数组单项接口
interface ChartDataItem {
  subject: string;
  value: number;
  baseline: number;
}

export const ScoreRadarChart = ({
  scores,
  type,
  baselineScore = 60,
}: ScoreRadarChartProps) => {
  // 防御性代码：防止未传数据时崩溃
  if (!scores) {
    return <div style={{ color: "#9ca3af" }}>Waiting for score data...</div>;
  }

  const chartData = scores.map((item) => ({
    ...item,
    baseline: baselineScore,
  }));

  // 将扁平的包含下划线的对象，映射为适合 Recharts 渲染的数组结构
  let colors = {
    stroke: "rgba(255,255,255,0.1)",
    axis: "rgba(230,241,255,0.72)",
    polarStroke: "#5ad7f5",
    polarFill: "rgba(33,158,188,0.16)",
    radarStroke: "#5ad7f5",
    radarFill: "rgba(33,158,188,0.16)",
  };

  if (type === "risk") {
    colors = {
      stroke: "rgba(255,255,255,0.08)",
      axis: "rgba(255,240,235,0.72)",
      polarStroke: "#ffb38a",
      polarFill: "rgba(255,140,80,0.14)",
      radarStroke: "#ffb38a",
      radarFill: "rgba(255,140,80,0.14)",
    };
  }

  if (type === "whale") {
    colors = {
      stroke: "rgba(255,255,255,0.08)",
      axis: "rgba(243,240,255,0.72)",
      polarStroke: "#a78bfa",
      polarFill: "rgba(139,92,246,0.14)",
      radarStroke: "#c4b5fd",
      radarFill: "rgba(139,92,246,0.14)",
    };
  }

  return (
    <div
      style={{
        width: "100%",
        height: 360,
        background: "transparent", // Web3 经典的暗黑科技感底色
        borderRadius: "12px",
        padding: "0px",
        boxSizing: "border-box",
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart cx="50%" cy="50%" outerRadius="75%" data={chartData}>
          {/* 蜘蛛网格线颜色 */}
          <PolarGrid stroke={colors.stroke} gridType="polygon" />

          {/* 五角形顶点的文字标签样式 */}
          <PolarAngleAxis
            dataKey="subject"
            tick={{ fill: colors.axis, fontSize: 12, fontWeight: 500 }}
          />

          {/* 径向轴（刻度轴）：范围 0-100，隐藏数字保持极简 */}
          <PolarRadiusAxis
            angle={30}
            domain={[0, 100]}
            stroke={colors.stroke}
            fill={colors.polarFill}
            tick={false}
            axisLine={false}
          />

          <Radar
            name="Market Baseline"
            dataKey="baseline"
            stroke="rgba(255, 255, 255, 0.15)" /* 基准线边框稍微清晰一点，防止看不清 */
            fill="rgba(255, 255, 255, 0.05)" /* 👈 满足你要求的 5% 低透明度白色填充 */
            fillOpacity={1} /* 设为 1，直接由 rgba 控制透明度更精准 */
            activeDot={false} /* 关闭基准线的悬浮圆点，避免抢戏 */
          />

          {/* 雷达图核心覆盖区域 */}
          <Radar
            name="Current State"
            dataKey="value"
            stroke={colors.radarStroke} /* 亮蓝色边框 */
            fill={colors.radarFill} /* 蓝色填充 */
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};
